import { useEffect, useRef, useState } from "react";
import type { Uppy, UppyFile } from "@uppy/core";
import { Dashboard } from "@uppy/react";
import Modal from "frontend/modal/modal";
import style from "./file-uploader.module.css";
import useOutsideRef from "frontend/utils/click-outside-handler";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import cn from "classnames";
import { nanoid } from "nanoid";
import ReactDOM from "react-dom";
import UploadFileIcon from "frontend/icons/upload-file";
import { useIsCIMReorderEnabled } from "utils/url-utils";

type FileUploaderProps = {
  uppy: Uppy;
  onUpload: (files: UppyFile[]) => void;
  onClose: () => void;
};

const DROP_MESSAGE = "Drop your file here or %{browseFiles}";

export function FileUploader({ uppy, onUpload, onClose }: FileUploaderProps) {
  const [numFilesWaiting, setNumFilesWaiting] = useState(() => uppy.getFiles().length);
  const [isUploading, setIsUploading] = useState(false);
  const isCimReorderEnabled = useIsCIMReorderEnabled();
  const numberOfFiles = uppy.getFiles().length;
  const id = useRef(nanoid());
  const ref = useRef<HTMLDivElement>(null);
  const [dashboardRef, setDashboardRef] = useState<HTMLDivElement | null>(null);
  // we listen to 'mouseup' on purpose. if we close the modal on 'mousedown' the canvas stage
  // gets the mouse-up event, and will pass it to the current drawing tool, which can create
  // a shape (this behaviour just feels wrong)
  useOutsideRef(ref, onClose, { event: "mouseup" });

  // TODO: retry after failure (design and function)
  function onFileAddOrRemove() {
    const pending = uppy.getFiles().length;
    setNumFilesWaiting(pending);
  }

  function onUploadComplete(result: any) {
    setIsUploading(false);
    setNumFilesWaiting(numFilesWaiting - result.successful.length);
    onClose();
  }

  function onUploadStart() {
    setIsUploading(true);
  }

  useEffect(() => {
    uppy.on("file-added", onFileAddOrRemove);
    uppy.on("file-removed", onFileAddOrRemove);
    uppy.on("complete", onUploadComplete);
    uppy.on("upload", onUploadStart);

    return () => {
      uppy.off("upload", onUploadStart);
      uppy.off("file-added", onFileAddOrRemove);
      uppy.off("file-removed", onFileAddOrRemove);
      uppy.off("complete", onUploadComplete);
    };
  }, []);

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "unlimited-file-uploader-showed");
  }, []);

  useEffect(() => {
    // We need to ensure the element is available
    if (dashboardRef) {
      return;
    }

    let animationFrameId: number;

    const checkForComponent = () => {
      const element = document.getElementById(id.current);
      // Check for the child element using the provided selector

      if (element) {
        setDashboardRef(element as HTMLDivElement);
        cancelAnimationFrame(animationFrameId);
        // You can add a callback function here if needed
      } else {
        // If not found, continue the check on the next animation frame
        animationFrameId = requestAnimationFrame(checkForComponent);
      }
    };

    // Start the polling loop
    animationFrameId = requestAnimationFrame(checkForComponent);

    // Cleanup function: this runs when the component unmounts
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [dashboardRef]);

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!dashboardRef || !isCimReorderEnabled) {
      return;
    }
    // Find the container and create a span to prepend content
    const container = document.querySelector(`#${id.current} .uppy-Dashboard-AddFiles-title`);
    if (container && !container.querySelector(".custom-prepend-container")) {
      const prependContainer = document.createElement("span");
      prependContainer.className = "custom-prepend-container";
      container.insertBefore(prependContainer, container.firstChild);
      setPortalContainer(prependContainer);
    }
  }, [dashboardRef, isCimReorderEnabled]);

  return (
    <Modal dimBackground={true}>
      <div ref={ref} className={cn(style.fileUploadModal, isCimReorderEnabled && style.cimUploadModal)}>
        <button onClick={onClose} className={style.closeButton}>
          &#10005;
        </button>
        <div className={style.uploadArea}>
          <div className={style.uploadTitle}>File upload</div>
          <Dashboard
            uppy={uppy}
            id={id.current}
            plugins={["ImageEditor"]}
            disableStatusBar={true}
            hideRetryButton={true}
            height="45vh"
            width="auto"
            className={cn(isCimReorderEnabled && style.cimDashboard)}
            locale={{
              strings: {
                browseFiles: "Browse files",
                dropHint: DROP_MESSAGE,
                dropPasteFiles: DROP_MESSAGE,
                dropPasteFolders: DROP_MESSAGE,
              },
            }}
          />
          {portalContainer && ReactDOM.createPortal(<UploadFileIcon />, portalContainer)}
          {numberOfFiles > 0 && !isUploading && (
            <button
              className={style.uploadButton}
              disabled={isUploading}
              onClick={() => {
                onUpload(uppy.getFiles());
              }}
            >
              {`Upload ${numberOfFiles > 1 ? "files" : "file"}`}
            </button>
          )}
          {isUploading && <div className={style.uploadInProgress}>Uploading...</div>}
        </div>
      </div>
    </Modal>
  );
}
